import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calculator, Baby, Home, Building2, Copy } from "lucide-react";

const ACCENT = "#FF1744";

const ASSETS = {
  childcare: {
    label: "Childcare Centre",
    icon: Baby,
    yieldPct: 6.0,
    growth: 4.0,
    months: 14,
    note: "15-yr lease structures typical in NSW growth corridors",
  },
  villa: {
    label: "Bespoke Villa / Duplex",
    icon: Home,
    yieldPct: 3.8,
    growth: 5.5,
    months: 11,
    note: "Capital-growth weighted residential play",
  },
  commercial: {
    label: "Commercial / Retail Hub",
    icon: Building2,
    yieldPct: 6.8,
    growth: 3.5,
    months: 18,
    note: "Mixed-use tenancy blends, medical + retail",
  },
};

const PRESETS = [
  { label: "Childcare Benchmark", asset: "childcare", land: 1.5, budget: 4.0, yield: "~6.5%" },
  { label: "Duplex / Villa", asset: "villa", land: 1.0, budget: 1.2, yield: "~5.5%" },
  { label: "Commercial Hub", asset: "commercial", land: 2.0, budget: 5.0, yield: "~7.0%" },
];

const fmtM = (v) => (v >= 1 ? `$${v.toFixed(1)}M` : `$${Math.round(v * 1000)}k`);

export const YieldCalculator = () => {
  const [asset, setAsset] = useState("childcare");
  const [land, setLand] = useState(1.2);
  const [budget, setBudget] = useState(4.5);
  const [hold, setHold] = useState(10);

  useEffect(() => {
    const onPreset = (e) => {
      if (ASSETS[e.detail]) setAsset(e.detail);
    };
    window.addEventListener("jai:preset-asset", onPreset);
    return () => window.removeEventListener("jai:preset-asset", onPreset);
  }, []);

  const a = ASSETS[asset];

  const result = useMemo(() => {
    const cost = land + budget;
    const annualIncome = cost * (a.yieldPct / 100);
    const futureValue = cost * Math.pow(1 + a.growth / 100, hold);
    const totalReturn = futureValue - cost + annualIncome * hold;
    const roi = (totalReturn / cost) * 100;
    const payback = cost / annualIncome;
    return { cost, annualIncome, roi, payback };
  }, [land, budget, hold, a]);

  return (
    <section id="calculator" className="px-6 sm:px-12 lg:px-20 py-28 lg:py-36 bg-[#111827]" data-testid="calculator-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-14"
        >
          <p className="font-display text-[11px] font-bold tracking-[0.35em] text-[#FF1744] mb-4 flex items-center gap-3">
            <Calculator size={14} /> CHAPTER 02 — YIELD &amp; DEVELOPMENT CALCULATOR
          </p>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.02] max-w-3xl">
            Run the numbers on a{" "}
            <em className="font-serif-lux italic font-semibold text-gradient-fire">NSW growth corridor.</em>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="glass rounded-2xl border border-white/10 p-8"
            data-testid="calculator-controls"
          >
            <p className="font-display text-xs font-bold tracking-[0.2em] text-white/50 mb-3">QUICK PRESETS</p>
            <div className="flex flex-wrap gap-2 mb-8" data-testid="calc-presets">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  data-testid={`preset-${p.asset}`}
                  onClick={() => {
                    setAsset(p.asset);
                    setLand(p.land);
                    setBudget(p.budget);
                  }}
                  className="rounded-full border border-[#FF1744]/40 bg-[#FF1744]/[0.07] px-4 py-2 font-display text-xs font-semibold text-white/80 hover:bg-[#FF1744]/15 hover:text-white hover:shadow-[0_0_16px_rgba(255,23,68,0.35)] hover:scale-[1.03] transition-all duration-300"
                >
                  {p.label} <span className="text-[#FF1744]">· {p.yield}</span>
                </button>
              ))}
            </div>

            <p className="font-display text-xs font-bold tracking-[0.2em] text-white/50 mb-4">ASSET CLASS</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
              {Object.entries(ASSETS).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const active = asset === key;
                return (
                  <button
                    key={key}
                    data-testid={`calc-asset-${key}`}
                    onClick={() => setAsset(key)}
                    className={`rounded-xl border p-4 text-left transition-all duration-300 ${
                      active
                        ? "border-[#FF1744] bg-[#FF1744]/10 shadow-[0_0_24px_rgba(255,23,68,0.25)]"
                        : "border-white/10 bg-white/[0.03] hover:border-white/30"
                    }`}
                  >
                    <Icon size={20} style={{ color: active ? ACCENT : "rgba(255,255,255,0.5)" }} />
                    <p className="mt-2 font-display text-xs font-semibold text-white leading-snug">{cfg.label}</p>
                  </button>
                );
              })}
            </div>

            {[
              { label: "LAND VALUE", value: land, set: setLand, min: 0.5, max: 5, step: 0.1, id: "land" },
              { label: "BUILD BUDGET", value: budget, set: setBudget, min: 1, max: 15, step: 0.25, id: "budget" },
              { label: "HOLD PERIOD", value: hold, set: setHold, min: 5, max: 20, step: 1, id: "hold", suffix: " yrs" },
            ].map((s) => (
              <div key={s.id} className="mb-8">
                <div className="flex justify-between items-baseline mb-3">
                  <label className="font-display text-xs font-bold tracking-[0.2em] text-white/50">{s.label}</label>
                  <span className="font-display text-lg font-bold text-white" data-testid={`calc-${s.id}-value`}>
                    {s.id === "hold" ? `${s.value} yrs` : fmtM(s.value)}
                  </span>
                </div>
                <input
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  value={s.value}
                  onChange={(e) => s.set(parseFloat(e.target.value))}
                  className="lux-range"
                  style={{ "--range-accent": ACCENT }}
                  data-testid={`calc-${s.id}-slider`}
                />
              </div>
            ))}

            <p className="text-[11px] text-white/40 leading-relaxed">{a.note}.</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="rounded-2xl border border-[#FF1744]/30 bg-gradient-to-b from-[#FF1744]/[0.07] to-transparent p-8 flex flex-col"
            data-testid="calculator-results"
          >
            <p className="font-display text-xs font-bold tracking-[0.2em] text-white/50 mb-8">PROJECTED OUTCOME</p>
            <div className="grid grid-cols-2 gap-6 flex-1">
              <div className="col-span-2">
                <p className="text-xs text-white/45 mb-1">Total project cost</p>
                <p className="font-display text-4xl font-extrabold text-white tracking-tight" data-testid="calc-result-cost">
                  {fmtM(result.cost)}
                </p>
              </div>
              <div>
                <p className="text-xs text-white/45 mb-1">Est. gross yield</p>
                <p className="font-display text-3xl font-bold text-[#FF1744]" data-testid="calc-result-yield">
                  {a.yieldPct.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-xs text-white/45 mb-1">Est. annual income</p>
                <p className="font-display text-3xl font-bold text-white" data-testid="calc-result-income">
                  {fmtM(result.annualIncome)}
                </p>
              </div>
              <div>
                <p className="text-xs text-white/45 mb-1">Build timeline</p>
                <p className="font-display text-3xl font-bold text-white" data-testid="calc-result-timeline">
                  ~{a.months} mo
                </p>
              </div>
              <div>
                <p className="text-xs text-white/45 mb-1">{hold}-yr projected ROI</p>
                <p className="font-display text-3xl font-bold text-[#FF1744]" data-testid="calc-result-roi">
                  +{result.roi.toFixed(0)}%
                </p>
              </div>
            </div>
            <button
              onClick={async () => {
                const text = [
                  "JAI HOMES — Feasibility Summary (Indicative Only)",
                  `Asset: ${a.label}`,
                  `Land: ${fmtM(land)} | Build: ${fmtM(budget)} | Total project cost: ${fmtM(result.cost)}`,
                  `Est. gross yield: ${a.yieldPct.toFixed(1)}% | Est. annual income: ${fmtM(result.annualIncome)}`,
                  `Build timeline: ~${a.months} months | ${hold}-yr projected ROI: +${result.roi.toFixed(0)}%`,
                  "— JAI HOMES · NSW Builder Licence 476816C · admin@jaihomes.com.au · (02) 7239 5450",
                ].join("\n");
                try {
                  await navigator.clipboard.writeText(text);
                } catch {
                  const ta = document.createElement("textarea");
                  ta.value = text;
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand("copy");
                  ta.remove();
                }
                toast.success("Feasibility summary copied to clipboard");
              }}
              data-testid="calc-copy-btn"
              className="mt-8 w-full rounded-full py-3 font-display text-sm font-bold text-white bg-gradient-to-r from-[#FF3D00] to-[#FF1744] hover:shadow-[0_0_24px_rgba(255,23,68,0.5)] hover:scale-[1.01] transition-all duration-300 inline-flex items-center justify-center gap-2"
            >
              <Copy size={15} /> Copy Feasibility Summary
            </button>
            <p className="mt-5 text-[11px] text-white/40 leading-relaxed border-t border-white/10 pt-5" data-testid="calc-disclaimer">
              Indicative only — modelled on typical NSW growth-corridor assumptions. Every site is
              unique; talk to our team for a feasibility tailored to your land.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
